<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Calcular liquidación</h1>
    </div>

    <!-- Paso 1: selección de colaborador cesado -->
    <wz-card v-if="step === 1" title="Paso 1 — Seleccionar colaborador cesado">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-select-search
            label="Empresa"
            required
            :items="companies"
            :value="form.companyId"
            defaultOption="Buscar empresa por nombre"
            @change="onCompanyChange"
          />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-select-search
            label="Colaborador cesado"
            required
            :items="eligibleOptions"
            :value="form.contractId"
            :disabled="!form.companyId"
            defaultOption="Seleccione un colaborador"
            @change="onContractChange"
          />
        </div>
      </div>

      <wz-alert
        v-if="form.companyId && !eligibleOptions.length"
        variant="info"
        icon="ri-information-line"
        class="w-full mb-3"
      >
        No hay contratos cesados sin liquidación en esta empresa.
      </wz-alert>

      <wz-alert v-if="prefillNotice" variant="warning" icon="ri-alert-line" class="w-full mb-3">
        {{ prefillNotice }}
      </wz-alert>

      <!-- Snapshot del cese -->
      <wz-card v-if="selectedContract" class="mb-3" title="Datos del cese">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/4 px-2 mb-2">
            <small class="text-muted block">Colaborador</small>
            <strong>{{ selectedContract.employeeFullName }}</strong>
          </div>
          <div class="w-full md:w-1/4 px-2 mb-2">
            <small class="text-muted block">DNI</small>
            <strong>{{ selectedContract.employeeDni || '-' }}</strong>
          </div>
          <div class="w-full md:w-1/4 px-2 mb-2">
            <small class="text-muted block">Fecha de cese</small>
            <strong v-local-date="selectedContract.terminationDate"></strong>
          </div>
          <div class="w-full md:w-1/4 px-2 mb-2">
            <small class="text-muted block">Motivo</small>
            <strong>{{ selectedContract.cessationReasonName || '-' }}</strong>
          </div>
        </div>

        <!-- Despido (03): subtipo obligatorio -->
        <div v-if="isDespido" class="flex flex-wrap -mx-2 mt-2">
          <div class="w-full md:w-1/3 px-2">
            <wz-select
              label="Subtipo de despido"
              required
              v-model="form.terminationSubtype"
              :items="subtypeOptions"
              defaultOption="Seleccione el subtipo"
            />
          </div>
        </div>

        <!-- Fallecimiento (09): beneficiario obligatorio -->
        <div v-if="isDeath" class="flex flex-wrap -mx-2 mt-2">
          <div class="w-full md:w-1/3 px-2 mb-2">
            <wz-input label="Nombre del beneficiario" required v-model="form.beneficiaryName" />
          </div>
          <div class="w-full md:w-1/3 px-2 mb-2">
            <wz-input label="DNI del beneficiario" required v-model="form.beneficiaryDni" />
          </div>
          <div class="w-full md:w-1/3 px-2 mb-2">
            <wz-input label="Parentesco" required v-model="form.beneficiaryRelationship" />
          </div>
        </div>
      </wz-card>

      <wz-alert v-if="calcError" variant="danger" icon="ri-error-warning-line" class="w-full mb-3">
        {{ calcError }}
      </wz-alert>

      <div class="flex justify-end gap-2 mt-2">
        <wz-button variant="light" @click="goToList">Cancelar</wz-button>
        <wz-button variant="primary" class="ml-2" :disabled="!canCalculate" @click="calculate">
          Calcular liquidación
        </wz-button>
      </div>
    </wz-card>

    <!-- Paso 2: vista previa del cálculo -->
    <wz-card v-else title="Paso 2 — Vista previa del cálculo">
      <div class="flex flex-wrap -mx-2 mb-3">
        <div class="w-full md:w-1/4 px-2">
          <small class="text-muted block">Colaborador</small>
          <strong>{{ result.employeeFullName }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <small class="text-muted block">DNI</small>
          <strong>{{ result.employeeDni || '-' }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <small class="text-muted block">Fecha de cese</small>
          <strong v-local-date="result.terminationDate"></strong>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <small class="text-muted block">Motivo</small>
          <strong>{{ result.cessationReasonName || '-' }}</strong>
        </div>
      </div>

      <wz-table>
        <thead>
          <tr>
            <th>Concepto</th>
            <th class="text-right">Bruto</th>
            <th class="text-right">AFP</th>
            <th class="text-right">ONP</th>
            <th class="text-right">EsSalud</th>
            <th class="text-right">IR</th>
            <th class="text-right">Neto</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in result.details" :key="d.id">
            <td>{{ conceptLabel(d.concept) }}</td>
            <td class="text-right"><span v-decimal:pen="d.grossAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.afpAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.onpAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.essaludAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.irAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.netAmount"></span></td>
          </tr>
          <tr v-if="!result.details || !result.details.length">
            <td colspan="7" class="text-center text-muted">Sin conceptos calculados.</td>
          </tr>
        </tbody>
        <tfoot v-if="result.details && result.details.length">
          <tr class="font-bold">
            <td class="text-right">Totales</td>
            <td class="text-right"><span v-decimal:pen="result.totalGross"></span></td>
            <td colspan="3" class="text-right">Deducciones: <span v-decimal:pen="result.totalDeductions"></span></td>
            <td class="text-right">Neto</td>
            <td class="text-right"><span v-decimal:pen="result.totalNet"></span></td>
          </tr>
        </tfoot>
      </wz-table>

      <!-- Notas de cálculo por concepto -->
      <div v-if="result.details && result.details.length" class="mt-3">
        <h6 class="font-bold mb-2">Detalle del cálculo</h6>
        <ul class="pl-3">
          <li v-for="d in result.details" :key="`note-${d.id}`" class="mb-1">
            <strong>{{ conceptLabel(d.concept) }}:</strong>
            <span class="text-muted">{{ d.calculationNotes || '-' }}</span>
          </li>
        </ul>
      </div>

      <p class="text-muted mt-2 mb-0">
        Se guardó como borrador. Calculado por {{ result.calculatedBy || '-' }}.
      </p>

      <div class="flex justify-end gap-2 mt-3">
        <wz-button variant="primary" @click="goToList">
          Volver a la lista
        </wz-button>
      </div>
    </wz-card>
  </div>
</template>

<script>
import * as moment from 'moment'
import { Alert } from '@wizepe/wizeui'
import { SeveranceService, CompanyService } from '@/core/services'

const CONCEPT_LABELS = {
  CTS_TRUNCA: 'CTS trunca',
  GRATIFICACION_TRUNCA: 'Gratificación trunca',
  BONIFICACION_9: 'Bonificación extraordinaria 9%',
  VACACIONES_TRUNCAS: 'Vacaciones truncas',
  VACACIONES_NO_GOZADAS: 'Vacaciones no gozadas',
  TRIPLE_VACACIONAL: 'Triple vacacional',
  INDEMNIZACION_DESPIDO: 'Indemnización por despido',
  REMUNERACION_PENDIENTE: 'Remuneración pendiente',
}

const SUBTYPE_OPTIONS = [
  { key: 'ARBITRARIO', label: 'Arbitrario' },
  { key: 'CAUSA_JUSTA', label: 'Causa justa' },
]

export default {
  data() {
    return {
      step: 1,
      companies: [],
      eligibleContracts: [],
      subtypeOptions: SUBTYPE_OPTIONS,
      calcError: null,
      prefillNotice: null,
      form: {
        companyId: null,
        contractId: null,
        terminationSubtype: null,
        beneficiaryName: '',
        beneficiaryDni: '',
        beneficiaryRelationship: '',
      },
      result: { details: [] },
    }
  },
  computed: {
    eligibleOptions() {
      // Incluye fecha de cese y motivo para distinguir múltiples contratos cesados del mismo colaborador.
      return this.eligibleContracts.map(c => ({
        key: c.contractId,
        label: `${c.employeeFullName || 'Sin nombre'} — ${c.employeeDni || 'sin DNI'} — Cese ${this.formatDate(c.terminationDate)} (${c.cessationReasonName || 'sin motivo'})`,
      }))
    },
    selectedContract() {
      return this.eligibleContracts.find(c => c.contractId === this.form.contractId) || null
    },
    isDespido() {
      return !!this.selectedContract && this.selectedContract.tregistroCode === '03'
    },
    isDeath() {
      return !!this.selectedContract && this.selectedContract.tregistroCode === '09'
    },
    canCalculate() {
      if (!this.form.contractId) return false
      if (this.isDespido && !this.form.terminationSubtype) return false
      if (this.isDeath && !(this.form.beneficiaryName && this.form.beneficiaryDni && this.form.beneficiaryRelationship)) {
        return false
      }
      return true
    },
  },
  methods: {
    conceptLabel(concept) {
      return CONCEPT_LABELS[concept] || concept
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '-'
    },
    goToList() {
      // Pasa companyId para que la lista preseleccione la empresa y refresque (muestra el borrador nuevo).
      const query = this.form.companyId ? { companyId: this.form.companyId } : {}
      this.$router.push({ name: 'severance-list', query })
    },
    resetContractSelection() {
      this.form.contractId = null
      this.form.terminationSubtype = null
      this.form.beneficiaryName = ''
      this.form.beneficiaryDni = ''
      this.form.beneficiaryRelationship = ''
      this.calcError = null
    },
    async onCompanyChange(companyId) {
      this.form.companyId = companyId
      this.eligibleContracts = []
      this.prefillNotice = null
      this.resetContractSelection()
      if (!companyId) return
      await this.loadEligible(companyId)
    },
    onContractChange(contractId) {
      this.form.contractId = contractId
      this.form.terminationSubtype = null
      this.form.beneficiaryName = ''
      this.form.beneficiaryDni = ''
      this.form.beneficiaryRelationship = ''
      this.calcError = null
      this.prefillNotice = null
    },
    async loadEligible(companyId) {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await SeveranceService.getEligibleContracts(companyId)
        this.eligibleContracts = resp.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los contratos cesados')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async calculate() {
      if (!this.canCalculate) return
      this.calcError = null
      const payload = { contractId: this.form.contractId }
      if (this.isDespido) payload.terminationSubtype = this.form.terminationSubtype
      if (this.isDeath) {
        payload.beneficiaryName = this.form.beneficiaryName
        payload.beneficiaryDni = this.form.beneficiaryDni
        payload.beneficiaryRelationship = this.form.beneficiaryRelationship
      }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await SeveranceService.calculate(payload)
        this.result = resp.payload || { details: [] }
        this.step = 2
      } catch (e) {
        console.error(e)
        this.calcError = e.errors ? e.errors.message : 'No se pudo calcular la liquidación'
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async loadCompanies() {
      try {
        const resp = await CompanyService.listAll()
        this.companies = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
      } catch (e) {
        console.error(e)
      }
    },
    // Prefill al entrar desde la ficha de contrato (query {contractId, companyId}).
    async applyQueryPrefill() {
      const { contractId, companyId } = this.$route.query
      if (!companyId) return
      this.form.companyId = Number(companyId)
      await this.loadEligible(this.form.companyId)
      if (!contractId) return
      const cid = Number(contractId)
      if (this.eligibleContracts.some(c => c.contractId === cid)) {
        this.onContractChange(cid)
      } else {
        this.prefillNotice = 'El contrato indicado no está disponible para liquidar (ya tiene una liquidación o no está cesado).'
      }
    },
  },
  async created() {
    await this.loadCompanies()
    await this.applyQueryPrefill()
  },
}
</script>
